import { FC, useMemo } from "react";
import _fromPairs from "lodash/fromPairs";

import { ControllableComponentPropsI } from "shared/lib/interfaces/ui";

import {
  QualificationCriteriaTable,
  QualificationCriteriaTablePropsI,
} from "./shared/base";
import {
  NO_BONUS_TIERS,
  QUALIFICATION_CRITERIA_TYPES,
} from "shared/lib/constants/qualification-criteria";
import {
  CampaignTiersI,
  QualificationCriteriaTableTogglesValueI,
} from "shared/lib/interfaces/qualification-criteria";
import {
  checkIfSelectedQCsQualify,
  getBonusCriteriaLabel,
  mapSelectedQCsToRequest,
} from "shared/lib/helpers/qualification-criteria";
import {
  QC_TABLE_TOGGLE_STYLES,
  QCCheckbox,
} from "shared/ui/qualification/table/shared/qc-checkbox";
import { ValueOfObjectFields } from "shared/lib/interfaces/utils";

export interface QualificationCriteriaTableWithTogglesPropsI
  extends
    QualificationCriteriaTablePropsI,
    ControllableComponentPropsI<QualificationCriteriaTableTogglesValueI> {
  isEditable?: boolean;
  /* Which bonus tiers this campaign offers. Defaults to none, so a caller that
   * does not pass it renders a plain one-tier table.
   */
  tiers?: CampaignTiersI;
  toggleStyle?: ValueOfObjectFields<typeof QC_TABLE_TOGGLE_STYLES>;
}

export const QualificationCriteriaTableWithToggles: FC<
  QualificationCriteriaTableWithTogglesPropsI
> = ({
  isEditable = true,
  tiers = NO_BONUS_TIERS,
  value: _value,
  onChange,
  texts,
  toggleStyle = QC_TABLE_TOGGLE_STYLES.REGULAR,
  ...restTableProps
}) => {
  const value = _value as QualificationCriteriaTableTogglesValueI;

  // Need that to only allow toggling the bonus criteria if all basic criteria
  // are met
  const areBasicQCsMet = useMemo(
    () =>
      _value
        ? checkIfSelectedQCsQualify(mapSelectedQCsToRequest(_value))
        : false,
    [_value]
  );

  const handleChange = (newValue: QualificationCriteriaTableTogglesValueI) => {
    if (!newValue) {
      onChange(newValue);
      return;
    }

    // Bonuses are mutually exclusive, so ticking one clears the others. This
    // is what makes the checkboxes behave as radio options without changing
    // the underlying value shape, which the rest of the flow already reads.
    const justTicked = tiers.bonusTypes.find(
      (type) => newValue[type] && !value?.[type]
    );
    const withExclusiveBonus = justTicked
      ? {
          ...newValue,
          ..._fromPairs(
            tiers.bonusTypes
              .filter((type) => type !== justTicked)
              .map((type) => [type, false])
          ),
        }
      : newValue;

    // A bonus cannot survive the base criteria failing.
    if (
      !checkIfSelectedQCsQualify(mapSelectedQCsToRequest(withExclusiveBonus))
    ) {
      onChange({
        ...withExclusiveBonus,
        ..._fromPairs(tiers.bonusTypes.map((type) => [type, false])),
      });
      return;
    }

    onChange(withExclusiveBonus);
  };

  const rightExtension = useMemo(() => {
    const head = {
      className: "text-center",
      content: "Met",
    };

    const cells = QUALIFICATION_CRITERIA_TYPES.map((type) => ({
      className: "text-center p-0 h-max",
      content: (
        <QCCheckbox
          toggleStyle={toggleStyle}
          type={type}
          isEditable={isEditable}
          value={value}
          onChange={handleChange}
        />
      ),
    }));

    return { head, cells };
  }, [isEditable, tiers, _value]);

  return (
    <QualificationCriteriaTable
      {...restTableProps}
      texts={texts}
      rightExtension={rightExtension}
      bottomExtension={{
        rows: tiers.bonusTypes.map((type) => ({
          type,
          cells: [
            { content: getBonusCriteriaLabel(type, tiers) },
            { content: texts[type] },
            {
              className: "text-center p-0",
              content: (
                <QCCheckbox
                  toggleStyle={toggleStyle}
                  type={type}
                  isEditable={areBasicQCsMet && isEditable}
                  value={value}
                  onChange={handleChange}
                />
              ),
            },
          ],
        })),
      }}
    />
  );
};
